import React from 'react'
import { CheckIcon } from '@heroicons/react/solid'

const SPARKLE_STYLES = `
@keyframes pd-benefit-enter {
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes pd-badge-gradient {
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}
@keyframes pd-sparkle-pop {
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  20%      { opacity: 1; transform: scale(1) rotate(45deg); }
  80%      { opacity: 0.7; transform: scale(1) rotate(90deg); }
}
@keyframes pd-list-shimmer {
  0%, 100% { transform: translateX(-150%) translateY(-150%); }
  50%      { transform: translateX(150%)  translateY(150%);  }
}

.pd-list {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
}
.pd-list::before {
  content: "";
  position: absolute; top: -50%; left: -50%;
  width: 200%; height: 200%;
  pointer-events: none;
  background: linear-gradient(
    100deg,
    transparent 0%,
    transparent 42%,
    rgba(153, 98, 231, 0.14) 50%,
    transparent 58%,
    transparent 100%
  );
  animation: pd-list-shimmer 9s ease-in-out infinite;
}
.pd-item {
  position: relative;
  opacity: 0;
  transform: translateX(-20px);
  animation: pd-benefit-enter 0.5s ease-out forwards;
  animation-delay: calc(var(--benefit-index, 0) * 0.08s);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.pd-item:hover { transform: translateX(5px); }

.pd-badge {
  position: relative;
  background: linear-gradient(135deg, #9962E7, #E383EC, #9962E7);
  background-size: 200% 100%;
  animation: pd-badge-gradient 4s ease infinite;
  box-shadow: 0 0 12px rgba(153, 98, 231, 0.35);
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}
.pd-item:hover .pd-badge {
  box-shadow: 0 0 22px rgba(227, 131, 236, 0.6);
  transform: scale(1.1) rotate(-6deg);
}

.pd-sparkle {
  position: absolute;
  pointer-events: none;
  color: #E383EC;
  filter: drop-shadow(0 0 4px rgba(227, 131, 236, 0.8));
  animation: pd-sparkle-pop 2.4s ease-in-out infinite;
  animation-delay: var(--sparkle-delay, 0s);
}

@media (prefers-reduced-motion: reduce) {
  .pd-item, .pd-badge, .pd-sparkle, .pd-list::before {
    animation: none !important;
  }
  .pd-item { opacity: 1; transform: none; }
}
`

const Sparkle = ({ className, style }) => (
  <svg viewBox="0 0 10 10" className={className} style={style} aria-hidden="true">
    <path d="M5 0 L6 4 L10 5 L6 6 L5 10 L4 6 L0 5 L4 4 Z" fill="currentColor" />
  </svg>
)

// Plain, quiet row for the free plan.
const PlainItem = ({ children }) => (
  <li className="group flex items-start gap-3 py-2.5 transition-transform duration-200 hover:translate-x-1">
    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 mt-0.5 rounded-full bg-clcpurple/10 ring-1 ring-clcpurple/30 group-hover:bg-clcpurple/15 group-hover:ring-clcpurple/50 transition-colors">
      <CheckIcon className="w-4 h-4 text-clcpurple dark:text-clcpurple-light" />
    </span>
    <span className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
      {children}
    </span>
  </li>
)

// Sparkly row for the paid plan.
const SparkleItem = ({ children, index = 0 }) => (
  <li
    className="pd-item group flex items-start gap-3 py-2.5"
    style={{ '--benefit-index': index }}
  >
    <span className="pd-badge flex-shrink-0 flex items-center justify-center w-7 h-7 mt-0.5 rounded-full">
      <CheckIcon className="w-4 h-4 text-white drop-shadow-sm relative z-10" />
      <Sparkle
        className="pd-sparkle w-2.5 h-2.5 -top-1 -right-1"
        style={{ '--sparkle-delay': `${index * 0.17}s` }}
      />
      <Sparkle
        className="pd-sparkle w-1.5 h-1.5 -bottom-1 -left-1"
        style={{ '--sparkle-delay': `${index * 0.17 + 1.2}s` }}
      />
    </span>
    <span className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
      {children}
    </span>
  </li>
)

const FREE_PERKS = [
  'Access to free events and events you paid for',
  'Access to courses you paid for'
]

const PAID_PERKS = [
  'Access to free events and events you paid for',
  'Access to courses you paid for',
  'Access to Members Only area in Discord',
  'Access to event recordings',
  'Access to in-house work opportunities',
  'Discounts on all courses',
  'Discounts on all events / workshops',
  'Access to events / workshops before anyone else',
]

const FreePlanDetails = () => (
  <ul className="px-6 pb-6 list-none">
    {FREE_PERKS.map((label, i) => (
      <PlainItem key={`free-${i}`}>{label}</PlainItem>
    ))}
  </ul>
)

const PaidPlanDetails = () => (
  <>
    <style>{SPARKLE_STYLES}</style>
    <ul className="pd-list px-6 pb-6 list-none">
      {PAID_PERKS.map((label, i) => (
        <SparkleItem key={`paid-${i}`} index={i}>{label}</SparkleItem>
      ))}
    </ul>
  </>
)

export { FreePlanDetails, PaidPlanDetails }
